import { Component, ElementRef, Input, ViewChild, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { IFormBase, TransferList } from '@models/index';
import { ApiService } from '@services/index';

@Component({
  selector: 'app-type-ahead',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
  ],
  templateUrl: './type-ahead.component.html',
  styleUrl: './type-ahead.component.scss'
})
export class TypeAheadComponent implements OnInit {
  @Input() containErrors = false;
  @Input() countError = false;

  @Input() ctrl!: IFormBase<string>;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('manualInput') manualInput!: ElementRef<HTMLInputElement>;

  public form!: FormGroup;
  public searchCtrl = new FormControl('');
  public filteredOptions!: Observable<TransferList[]>;
  public selectedOptions: TransferList[] = [];
  public isLoading: boolean = false;
  public debounceDelay = 300;
  private minimumStringLengthForAnAPICall = 3;
  private optionSelected = false;
  private blurTimeout: any;
  public invalidParenthesisError: boolean = false;

  constructor(
    private formGroupDirective: FormGroupDirective,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.form = this.formGroupDirective.control;

    this.filteredOptions = this.searchCtrl.valueChanges.pipe(
      debounceTime(this.debounceDelay),
      distinctUntilChanged(),
      switchMap((inputValue) => {
          const value = inputValue ? inputValue.trim(): ""
          const isNum= this.isNumeric(value)
          this.isLoading = true;
          this.cdr.detectChanges();
          if ((inputValue === null || inputValue.trim() === '') ) {
            this.isLoading = false;
            return of([]);
          }

          if ((!isNum && value.length < this.minimumStringLengthForAnAPICall) ) {
            this.isLoading = false;
            return of([]);
          }

        return this.apiService.getInputFieldOptions(this.ctrl, inputValue, false, this.form.value).pipe(
          map(response => {
            const options = response.Data
              .map(option => ({ id: option[1], value: option[0] }));

            return options.filter(option => !this.isOptionSelected(option));
          }),
          catchError(error => {
            console.error(error);
            return of([]);
          }),
          finalize(() => {
            this.isLoading = false;
            this.cdr.detectChanges();
          })
        );
      })
    );
  }

  public remove(option: TransferList): void {
    const index = this.selectedOptions.indexOf(option);

    if (index >= 0) {
      this.selectedOptions.splice(index, 1);
    }
    this.form.get(this.ctrl.name)?.patchValue(this.selectedOptions);
  }

  public selected(event?: MatAutocompleteSelectedEvent): void {
    this.optionSelected = true
    const selectedObject = event ? { id: event.option.value.id, value: event.option.value.value } : null ;
    this.invalidParenthesisError = false;

    if(event && selectedObject){
      this.selectIfNotSelected(selectedObject);
      this.searchInput.nativeElement.value = '';
      this.searchCtrl.setValue(null);
    } else {
      const value = this.searchCtrl.value?.replace(/^[()]+|[()]+$/g, '').trim();
      const isNum = this.isNumeric(value)
      if(value && isNum ){
        if(/[()]/.test(value.slice(1, -1))){
          this.invalidParenthesisError = true;
        }
        else {
            value.split(/[\s,]+/).map(item => {
            if (item && !this.selectedOptions.some(option => option.id === item)) {
              this.selectedOptions.push({
                id: item,
                value: item
              });
            }
          });
          this.form.get(this.ctrl.name)?.patchValue(this.selectedOptions);
          this.searchInput.nativeElement.value = '';
          this.searchCtrl.setValue(null);
        }

      }
    }

  }

  public selectIfNotSelected(option: TransferList): void {
    const exists = this.selectedOptions.some(selected => selected.id.toString() === option.id.toString());
    if (!exists) {
      this.selectedOptions.push(option);
      this.form.get(this.ctrl.name)?.patchValue(this.selectedOptions);
    }
  }

  public isOptionSelected(option: TransferList): boolean {
    return this.selectedOptions.some(selected => selected.id === option.id);
  }

  public isNumeric(value): boolean {
    return /^(?!0\d)[0-9,\s()*]*$/.test(value);
  }

  public onBlur(): void {
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
    }
    this.blurTimeout = setTimeout(() => {
      if (!this.optionSelected) {
        this.selected()
      }
      this.optionSelected = false;
    }, 300);
  }

  public errorText(): string {
      if (this.invalidParenthesisError) {
        return 'Parenthesis is not allowed as a separator. Please use commas or spaces.';
      }
      if (this.containErrors) {
        return `${this.ctrl.label} is required.`;
      }
      if (this.countError) {
        return `Please select only ${this.ctrl.countOfSelect} ${this.ctrl.label}.`;
      }
      return '';
    }
}
